/**
 * <aya-treatment-carousel> — metaobject-driven treatment carousel.
 *
 * Real horizontal scroll (CSS scroll-snap on the track) with dot controls,
 * plus tab filtering by Treatable Concern. Deliberately its OWN custom
 * element, separate from <aya-treatment-grid> (shared with
 * location-grid-01.liquid, which pages by hiding/showing cards instead of
 * scrolling) — see the comment atop sections/treatment-grid-01.liquid.
 */
class AyaTreatmentCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.aya-treatment-carousel__track');
    this.filters = Array.from(this.querySelectorAll('.aya-treatment-carousel__filter'));
    this.select = this.querySelector('.aya-treatment-carousel__select');
    this.cards = Array.from(this.querySelectorAll('.aya-treatment-carousel__card'));
    this.dotsEl = this.querySelector('.aya-treatment-carousel__dots');

    if (!this.track || !this.cards.length) return;

    this.activeConcern = null;
    this.visibleCards = this.cards;

    this.onScroll = this.debounce(() => this.syncDotsFromScroll(), 100);
    this.onResize = this.debounce(() => this.buildDots(), 150);

    this.filters.forEach((button) => {
      button.addEventListener('click', () => this.selectConcern(button));
    });
    this.select?.addEventListener('change', () => {
      const button = this.filters.find((filter) => filter.dataset.concern === this.select.value);
      if (button) {
        this.selectConcern(button);
      } else {
        this.clearConcern();
      }
    });
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    // Theme editor: a Tab block's shopify_attributes sit on its filter button,
    // so selecting that block in the sidebar shows its cards.
    this.onBlockSelect = (event) => {
      const button = this.filters.find((filter) => filter === event.target);
      if (button) this.selectConcern(button);
    };
    this.addEventListener('shopify:block:select', this.onBlockSelect);

    const defaultConcern = this.dataset.defaultConcern;
    const preset = defaultConcern
      ? this.filters.find((button) => button.dataset.concern === defaultConcern)
      : null;

    if (preset) {
      this.selectConcern(preset);
    } else {
      this.applyVisibility();
      this.buildDots();
    }
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }

  selectConcern(button) {
    this.filters.forEach((filter) => {
      const isActive = filter === button;
      filter.classList.toggle('is-active', isActive);
      filter.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.activeConcern = button.dataset.concern || null;
    if (this.select) this.select.value = this.activeConcern;
    this.applyVisibility();
    this.track.scrollLeft = 0;
    this.buildDots();
  }

  clearConcern() {
    this.filters.forEach((filter) => {
      filter.classList.remove('is-active');
      filter.setAttribute('aria-selected', 'false');
    });
    this.activeConcern = null;
    this.applyVisibility();
    this.track.scrollLeft = 0;
    this.buildDots();
  }

  applyVisibility() {
    this.visibleCards = this.cards.filter((card) => {
      if (!this.activeConcern) return true;
      const tokens = (card.dataset.concerns || '').split(/\s+/).filter(Boolean);
      return tokens.includes(this.activeConcern);
    });

    this.cards.forEach((card) => {
      const visible = this.visibleCards.includes(card);
      card.hidden = !visible;
      card.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) {
        card.removeAttribute('tabindex');
      } else {
        card.setAttribute('tabindex', '-1');
      }
    });
  }

  /** How many cards currently fit in one view of the track. */
  cardsPerView() {
    const first = this.visibleCards[0];
    if (!first) return 1;
    const trackWidth = this.track.clientWidth;
    const cardWidth = first.getBoundingClientRect().width;
    if (!cardWidth) return 1;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    return Math.max(1, Math.round((trackWidth + gap) / (cardWidth + gap)));
  }

  buildDots() {
    if (!this.dotsEl) return;
    this.dotsEl.innerHTML = '';

    const perView = this.cardsPerView();
    const pageCount = Math.max(1, Math.ceil(this.visibleCards.length / perView));
    this.dotsEl.hidden = pageCount <= 1;

    for (let i = 0; i < pageCount; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `aya-treatment-carousel__dot${i === 0 ? ' is-active' : ''}`;
      dot.setAttribute('aria-label', `Show treatments page ${i + 1}`);
      // The dots container carries role="tablist" (markup, see this section's
      // .liquid file); WAI-ARIA requires a tablist's children to be role="tab"
      // — Lighthouse/axe flags this as `aria-required-children` otherwise.
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      dot.addEventListener('click', () => {
        // Set active state here rather than waiting on the scroll listener
        // to figure it out — scroll events land asynchronously (sometimes
        // by a whole animation's length), so a click should give instant
        // feedback rather than a visible lag before the dot lights up.
        Array.from(this.dotsEl.children).forEach((d, di) => {
          d.classList.toggle('is-active', di === i);
          d.setAttribute('aria-selected', di === i ? 'true' : 'false');
        });
        this.scrollToPage(i);
      });
      this.dotsEl.appendChild(dot);
    }
  }

  /**
   * A card's offset from the track's scrollable origin. `offsetLeft` is
   * relative to the nearest POSITIONED ancestor, which is often not the
   * track itself (neither element sets `position` here) — using it produced
   * garbage coordinates and silently broke both scroll-to-page and dot sync.
   * getBoundingClientRect() is relative to the viewport for both elements,
   * so the difference is exact regardless of the positioning context.
   */
  offsetWithinTrack(card) {
    return card.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
  }

  scrollToPage(index) {
    const perView = this.cardsPerView();
    const target = this.visibleCards[index * perView];
    if (!target || !this.track) return;
    // A plain assignment (not scrollTo({behavior:'smooth'})) — the smooth
    // option fights scroll-snap-type and gets reset to ~0 mid-animation in
    // testing. CSS `scroll-behavior: smooth` on the track (opt-in via
    // prefers-reduced-motion, see custom.css) animates this the same way
    // without that interruption, and degrades to an instant jump when the
    // shopper prefers reduced motion.
    this.track.scrollLeft = this.offsetWithinTrack(target);
  }

  /** Keeps the dots honest when a shopper swipes/scrolls the track by hand. */
  syncDotsFromScroll() {
    if (!this.dotsEl || !this.visibleCards.length) return;
    const dots = Array.from(this.dotsEl.children);
    if (!dots.length) return;

    const perView = this.cardsPerView();
    const scrollLeft = this.track.scrollLeft;

    let closestIndex = 0;
    let closestDistance = Infinity;
    this.visibleCards.forEach((card, i) => {
      const distance = Math.abs(this.offsetWithinTrack(card) - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });

    const activePage = Math.min(dots.length - 1, Math.floor(closestIndex / perView));
    dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === activePage);
      dot.setAttribute('aria-selected', i === activePage ? 'true' : 'false');
    });
  }
}

if (!customElements.get('aya-treatment-carousel')) {
  customElements.define('aya-treatment-carousel', AyaTreatmentCarousel);
}
